import Modal from "react-bootstrap/Modal"
import Box from "@mui/material/Box"
import Row from "react-bootstrap/esm/Row"
import Col from "react-bootstrap/esm/Col"
import SurveyComponent from "pages/RMO/components/cancerTemplate/Template"
import defaultSurveyConfig from "pages/RMO/components/cancerTemplateForm/template"
import { useEffect, useState } from "react"
import React from "react"
import PermissionUtils from "core/store/utils/PermissionUtils"
import Toast from "components/Toast"
import { getConsentForms, saveConsentForm } from "core/services/api"
import { getMandatoryForms } from "core/services/api/mrdService"
import { getConsentTemplate } from "core/services/api/patientDiagnosisService"
import moment from "moment"
import { useSelector } from "react-redux"
import Alert from 'react-bootstrap/esm/Alert'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faArrowRightToCity, faCircleExclamation } from '@fortawesome/free-solid-svg-icons'
import Button from 'react-bootstrap/esm/Button'
import { calculateAge } from "core/utils/date"

const MandatoryConsentForm = (
  { appointmentDetails,
    appointmentId,
    staffDetails,
    patientDetail,
    smartSummaryData,
    closeModal = () => { },
    showWarningOnly = false,
    readOnly = false,
    children
  }: {
    appointmentDetails: any,
    appointmentId: any,
    staffDetails: any,
    patientDetail: any,
    smartSummaryData: any,
    closeModal: any,
    showWarningOnly: boolean,
    readOnly: boolean,
    children: any
  }) => {
  const [fetchingMandatoryForms, setFetchingMandatoryForms] = useState<boolean>(true)
  const [fetchingSavedForms, setFetchingSavedForms] = useState<boolean>(true)
  const [savedForms, setSavedForms] = useState<any>([])
  const [pendingForms, setPendingForms] = useState<any>([])
  const [formTemplate, setFormTemplate] = useState<any>()
  const [formData, setFormData] = useState<any>({})
  const [visible, setVisible] = useState<boolean>(false)
  const [showModal, setShowModal] = useState<boolean>(false)
  const [saved, setSaved] = useState<boolean>(false)
  const [accessDenied, setAccessDenied] = useState<boolean>(false)
  const [selectedForm, setSelectedForm] = useState<any>()
  const [showChildren, setShowChildren] = useState<boolean>(true)
  const [displayToast, setDisplayToast] = useState<any>(null);

  const userDetail = useSelector((state: any) => state?.user?.userDetail)
  const userRole = useSelector((state: any) => state?.user?.role)

  const handleFormComplete = (data: any) => {
    const payload = {
      centerId: appointmentDetails?.centerId,
      patientId: appointmentDetails?.patientId,
      appointmentId: appointmentId,
      formTemplateId: selectedForm?.id,
      data: data,
    }
    saveConsentForm(payload)
      .then((res: any) => {
        setShowChildren(false)
        getPendingForms()
        setSaved(true)
        setSelectedForm("")
      })
      .catch((err: any) => {
        console.error("Error saving consent form:", err);
        setDisplayToast({
          text: `${err?.response?.data?.message || "Failed to save form"}`,
          type: "error",
        });
      });
  }

  const getPendingForms = () => {
    getMandatoryForms(appointmentId)
      .then((res: any) => {
        const notFilled = res?.filter((each: any) => !each?.isFilled)
        if (notFilled?.length > 0) {
          setPendingForms(notFilled)
          setVisible(true)
        } else {
          setVisible(false)
        }
      })
      .catch((err: any) => {
        console.error(err?.response?.data?.message)
      })
      .finally(() => {
        setFetchingMandatoryForms(false)
      })
  }

  const getSavedForms = () => {
    getConsentForms({
      appointmentId: appointmentId,
      type: "SAVED",
    })
      .then((res: any) => {
        setSavedForms(res);
      })
      .catch((err: any) => {
        console.error("Error in fetching consent forms", err);
      })
      .finally(() => {
        setFetchingSavedForms(false)
      })
  }

  const setLoading = (status: boolean) => {
    setFetchingMandatoryForms(status)
    setFetchingSavedForms(status)
  }

  useEffect(() => {
    if (!showWarningOnly && visible && !readOnly) {
      setShowModal(true)
    } else {
      setShowModal(false)
    }
  }, [visible])

  useEffect(() => {
    if (appointmentDetails?.appointmentType === "IPD" || appointmentDetails?.appointmentType === "HC" && appointmentId) {
      getPendingForms()
      getSavedForms()
    } else {
      setLoading(false)
    }
  }, [appointmentDetails, appointmentId])

  useEffect(() => {
    if (selectedForm?.id) {
      setFormTemplate({})
      getConsentTemplate("CONSENT", selectedForm?.id)
        .then((res: any) => {
          const isWriteOnly = res?.pages?.every((each: any) => each?.readOnly)
          if (isWriteOnly) {
            setFormTemplate({})
            setShowModal(false)
            setAccessDenied(true)
          } else {
            setFormTemplate(res)
            const temp: any = {}
            res?.pages?.forEach((each: any) => {
              each?.elements?.forEach((element: any) => {
                if (Object?.keys(element)?.includes("elements")) {
                  element?.elements?.forEach((ele: any) => {
                    const nodeName = ele?.name
                    if (Object?.keys(ele)?.includes("elements")) {
                      ele?.elements?.forEach((e: any) => {
                        const nodeName = e?.name
                        if (nodeName === "consultantName") {
                          temp["consultantName"] = staffDetails?.name
                        } else if (nodeName === "consultantSignature" || nodeName === "dieticianSignature") {
                          temp[`${nodeName}`] = staffDetails?.signaturePicUrl
                        } else if (nodeName === "evaluatingConsultantSignature") {
                          temp["evaluatingConsultantSignature"] = staffDetails?.signaturePicUrl
                        } else if (nodeName === "patientName" || nodeName === "nameOfPatient" || nodeName === "pName") {
                          temp[`${nodeName}`] = patientDetail?.isVIP ? patientDetail?.mocId : patientDetail?.name
                        } else if (nodeName === "patientId" || nodeName === "UHID" || nodeName === "uhid") {
                          temp[`${nodeName}`] = patientDetail?.mocId
                        } else if (nodeName === "patientDob") {
                          temp["patientDob"] = patientDetail?.dob
                        } else if (nodeName === "patientAgeGender" || nodeName === "ageGender") {
                          temp[`${nodeName}`] = `${calculateAge(patientDetail?.dob)}/${patientDetail?.gender}`
                        } else if (nodeName === "patientAge" || nodeName === "age") {
                          temp[`${nodeName}`] = calculateAge(patientDetail?.dob)
                        } else if (nodeName === "patientGender" || nodeName === "gender" || nodeName === "sex") {
                          temp[`${nodeName}`] = patientDetail?.gender
                        } else if (nodeName === "patientAddress" || nodeName === "Address") {
                          temp[`${nodeName}`] = patientDetail?.addressInfo ?
                            `${patientDetail?.addressInfo?.addressLine1 ? patientDetail?.addressInfo?.addressLine1 + ", " : ""}${patientDetail?.addressInfo?.addressLine2 ? patientDetail?.addressInfo?.addressLine2 + ", " : ""}${patientDetail?.addressInfo?.suburb ? patientDetail?.addressInfo?.suburb + ", " : ""}${patientDetail?.addressInfo?.district ? patientDetail?.addressInfo?.district + ", " : ""}${patientDetail?.addressInfo?.state ? patientDetail?.addressInfo?.state + ", " : ""}${patientDetail?.addressInfo?.country ? patientDetail?.addressInfo?.country + ", " : ""}${patientDetail?.addressInfo?.pincode ? patientDetail?.addressInfo?.pincode : ""}`
                            : ""
                        } else if (nodeName === "patientMobileNo") {
                          temp[`${nodeName}`] = patientDetail?.phoneNumber
                        } else if (nodeName === "patientEmailId") {
                          temp[`${nodeName}`] = patientDetail?.email
                        } else if (nodeName === "patientEmailId") {
                          temp[`${nodeName}`] = patientDetail?.email
                        } else if (nodeName === "todaysDate" ||
                          nodeName === "consultantSignatureDate" ||
                          nodeName === "patientSignatureDate" ||
                          nodeName === "relativeSignatureDate" ||
                          nodeName === "staffSignatureDate" ||
                          nodeName === "date" ||
                          nodeName === "nameOfRelativeOrRelationDate" ||
                          nodeName === "consultantDate" ||
                          nodeName === "patientDate" ||
                          nodeName === "dateNameOfPersonProvidingInformation" ||
                          nodeName === "admittedOnDate" ||
                          nodeName === "dateOfApplication" ||
                          nodeName === "witnessDate" ||
                          nodeName === "nurseSignatureDate" ||
                          nodeName === "dateOfAdmission" ||
                          nodeName === "todaysDate_TPA"
                        ) {
                          temp[`${nodeName}`] = moment().format("YYYY-MM-DD")
                        } else if (nodeName === "patientTime" ||
                          nodeName === "relativeTime" ||
                          nodeName === "consultantSignatureTime" ||
                          nodeName === "relativeSignatureDate" ||
                          nodeName === "staffSignatureTime" ||
                          nodeName === "timeOfAdmission" ||
                          nodeName === "timeOfDischarge" ||
                          nodeName === "hospitalStaffTime"
                        ) {
                          temp[`${nodeName}`] = moment().format("HH:MM")
                        } else if (nodeName === "currentDateTime" ||
                          nodeName === "consultantDateTime" ||
                          nodeName === "relativeDateTime" ||
                          nodeName === "receiverDateTime" ||
                          nodeName === "applicantDateTime") {
                          temp[`${nodeName}`] = moment().format("YYYY-MM-DDTHH:MM")
                        } else if (nodeName === "diagnosis") {
                          let diagnosisStr = ""
                          smartSummaryData?.diagnosis?.forEach((each: any) => {
                            diagnosisStr += `${each?.diagnosis ? each?.diagnosis : "NA"} (${each?.cancerType ? each?.cancerType : ""}). `
                          })
                          temp[`${nodeName}`] = diagnosisStr
                        } else if (nodeName === "centerName") {
                          temp[`${nodeName}`] = userDetail?.currentCenter?.centerName
                        }
                      })
                    } else {
                      if (nodeName === "consultantName") {
                        temp["consultantName"] = staffDetails?.name
                      } else if (nodeName === "consultantSignature" || nodeName === "dieticianSignature") {
                        temp[`${nodeName}`] = staffDetails?.signaturePicUrl
                      } else if (nodeName === "evaluatingConsultantSignature") {
                        temp["evaluatingConsultantSignature"] = staffDetails?.signaturePicUrl
                      } else if (nodeName === "patientName" || nodeName === "nameOfPatient" || nodeName === "pName") {
                        temp[`${nodeName}`] = patientDetail?.isVIP ? patientDetail?.mocId : patientDetail?.name
                      } else if (nodeName === "patientId" || nodeName === "UHID" || nodeName === "uhid") {
                        temp[`${nodeName}`] = patientDetail?.mocId
                      } else if (nodeName === "patientDob") {
                        temp["patientDob"] = patientDetail?.dob
                      } else if (nodeName === "patientAgeGender" || nodeName === "ageGender") {
                        temp[`${nodeName}`] = `${calculateAge(patientDetail?.dob)}/${patientDetail?.gender}`
                      } else if (nodeName === "patientAge" || nodeName === "age") {
                        temp[`${nodeName}`] = calculateAge(patientDetail?.dob)
                      } else if (nodeName === "patientGender" || nodeName === "gender" || nodeName === "sex") {
                        temp[`${nodeName}`] = patientDetail?.gender
                      } else if (nodeName === "patientAddress" || nodeName === "Address") {
                        temp[`${nodeName}`] = patientDetail?.addressInfo ?
                          `${patientDetail?.addressInfo?.addressLine1 ? patientDetail?.addressInfo?.addressLine1 + ", " : ""}${patientDetail?.addressInfo?.addressLine2 ? patientDetail?.addressInfo?.addressLine2 + ", " : ""}${patientDetail?.addressInfo?.suburb ? patientDetail?.addressInfo?.suburb + ", " : ""}${patientDetail?.addressInfo?.district ? patientDetail?.addressInfo?.district + ", " : ""}${patientDetail?.addressInfo?.state ? patientDetail?.addressInfo?.state + ", " : ""}${patientDetail?.addressInfo?.country ? patientDetail?.addressInfo?.country + ", " : ""}${patientDetail?.addressInfo?.pincode ? patientDetail?.addressInfo?.pincode : ""}`
                          : ""
                      } else if (nodeName === "patientMobileNo") {
                        temp[`${nodeName}`] = patientDetail?.phoneNumber
                      } else if (nodeName === "patientEmailId") {
                        temp[`${nodeName}`] = patientDetail?.email
                      } else if (nodeName === "patientEmailId") {
                        temp[`${nodeName}`] = patientDetail?.email
                      } else if (nodeName === "todaysDate" ||
                        nodeName === "consultantSignatureDate" ||
                        nodeName === "patientSignatureDate" ||
                        nodeName === "relativeSignatureDate" ||
                        nodeName === "staffSignatureDate" ||
                        nodeName === "date" ||
                        nodeName === "nameOfRelativeOrRelationDate" ||
                        nodeName === "consultantDate" ||
                        nodeName === "patientDate" ||
                        nodeName === "dateNameOfPersonProvidingInformation" ||
                        nodeName === "admittedOnDate" ||
                        nodeName === "dateOfApplication" ||
                        nodeName === "witnessDate" ||
                        nodeName === "nurseSignatureDate" ||
                        nodeName === "dateOfAdmission" ||
                        nodeName === "todaysDate_TPA"
                      ) {
                        temp[`${nodeName}`] = moment().format("YYYY-MM-DD")
                      } else if (nodeName === "patientTime" ||
                        nodeName === "relativeTime" ||
                        nodeName === "consultantSignatureTime" ||
                        nodeName === "relativeSignatureDate" ||
                        nodeName === "staffSignatureTime" ||
                        nodeName === "timeOfAdmission" ||
                        nodeName === "timeOfDischarge" ||
                        nodeName === "hospitalStaffTime"
                      ) {
                        temp[`${nodeName}`] = moment().format("HH:MM")
                      } else if (nodeName === "currentDateTime" ||
                        nodeName === "consultantDateTime" ||
                        nodeName === "relativeDateTime" ||
                        nodeName === "receiverDateTime" ||
                        nodeName === "applicantDateTime") {
                        temp[`${nodeName}`] = moment().format("YYYY-MM-DDTHH:MM")
                      } else if (nodeName === "diagnosis") {
                        let diagnosisStr = ""
                        smartSummaryData?.diagnosis?.forEach((each: any) => {
                          diagnosisStr += `${each?.diagnosis ? each?.diagnosis : "NA"} (${each?.cancerType ? each?.cancerType : ""}). `
                        })
                        temp[`${nodeName}`] = diagnosisStr
                      } else if (nodeName === "centerName") {
                        temp[`${nodeName}`] = userDetail?.currentCenter?.centerName
                      }
                    }
                  })
                } else {
                  const nodeName = element?.name
                  if (nodeName === "consultantName") {
                    temp["consultantName"] = staffDetails?.name
                  } else if (nodeName === "consultantSignature" || nodeName === "dieticianSignature") {
                    temp[`${nodeName}`] = staffDetails?.signaturePicUrl
                  } else if (nodeName === "evaluatingConsultantSignature") {
                    temp["evaluatingConsultantSignature"] = staffDetails?.signaturePicUrl
                  } else if (nodeName === "patientName" || nodeName === "nameOfPatient" || nodeName === "pName") {
                    temp[`${nodeName}`] = patientDetail?.isVIP ? patientDetail?.mocId : patientDetail?.name
                  } else if (nodeName === "patientId" || nodeName === "UHID" || nodeName === "uhid") {
                    temp[`${nodeName}`] = patientDetail?.mocId
                  } else if (nodeName === "patientDob") {
                    temp["patientDob"] = patientDetail?.dob
                  } else if (nodeName === "patientAgeGender" || nodeName === "ageGender") {
                    temp[`${nodeName}`] = `${calculateAge(patientDetail?.dob)}/${patientDetail?.gender}`
                  } else if (nodeName === "patientAge" || nodeName === "age") {
                    temp[`${nodeName}`] = calculateAge(patientDetail?.dob)
                  } else if (nodeName === "patientGender" || nodeName === "gender" || nodeName === "sex") {
                    temp[`${nodeName}`] = patientDetail?.gender
                  } else if (nodeName === "patientAddress" || nodeName === "Address") {
                    temp[`${nodeName}`] = patientDetail?.addressInfo ?
                      `${patientDetail?.addressInfo?.addressLine1 ? patientDetail?.addressInfo?.addressLine1 + ", " : ""}${patientDetail?.addressInfo?.addressLine2 ? patientDetail?.addressInfo?.addressLine2 + ", " : ""}${patientDetail?.addressInfo?.suburb ? patientDetail?.addressInfo?.suburb + ", " : ""}${patientDetail?.addressInfo?.district ? patientDetail?.addressInfo?.district + ", " : ""}${patientDetail?.addressInfo?.state ? patientDetail?.addressInfo?.state + ", " : ""}${patientDetail?.addressInfo?.country ? patientDetail?.addressInfo?.country + ", " : ""}${patientDetail?.addressInfo?.pincode ? patientDetail?.addressInfo?.pincode : ""}`
                      : ""
                  } else if (nodeName === "patientMobileNo") {
                    temp[`${nodeName}`] = patientDetail?.phoneNumber
                  } else if (nodeName === "patientEmailId") {
                    temp[`${nodeName}`] = patientDetail?.email
                  } else if (nodeName === "patientEmailId") {
                    temp[`${nodeName}`] = patientDetail?.email
                  } else if (nodeName === "todaysDate" ||
                    nodeName === "consultantSignatureDate" ||
                    nodeName === "patientSignatureDate" ||
                    nodeName === "relativeSignatureDate" ||
                    nodeName === "staffSignatureDate" ||
                    nodeName === "date" ||
                    nodeName === "nameOfRelativeOrRelationDate" ||
                    nodeName === "consultantDate" ||
                    nodeName === "patientDate" ||
                    nodeName === "dateNameOfPersonProvidingInformation" ||
                    nodeName === "admittedOnDate" ||
                    nodeName === "dateOfApplication" ||
                    nodeName === "witnessDate" ||
                    nodeName === "nurseSignatureDate" ||
                    nodeName === "dateOfAdmission" ||
                    nodeName === "todaysDate_TPA"
                  ) {
                    temp[`${nodeName}`] = moment().format("YYYY-MM-DD")
                  } else if (nodeName === "patientTime" ||
                    nodeName === "relativeTime" ||
                    nodeName === "consultantSignatureTime" ||
                    nodeName === "relativeSignatureDate" ||
                    nodeName === "staffSignatureTime" ||
                    nodeName === "timeOfAdmission" ||
                    nodeName === "timeOfDischarge" ||
                    nodeName === "hospitalStaffTime"
                  ) {
                    temp[`${nodeName}`] = moment().format("HH:MM")
                  } else if (nodeName === "currentDateTime" ||
                    nodeName === "consultantDateTime" ||
                    nodeName === "relativeDateTime" ||
                    nodeName === "receiverDateTime" ||
                    nodeName === "applicantDateTime") {
                    temp[`${nodeName}`] = moment().format("YYYY-MM-DDTHH:MM")
                  } else if (nodeName === "diagnosis") {
                    let diagnosisStr = ""
                    smartSummaryData?.diagnosis?.forEach((each: any) => {
                      diagnosisStr += `${each?.diagnosis ? each?.diagnosis : "NA"} (${each?.cancerType ? each?.cancerType : ""}). `
                    })
                    temp[`${nodeName}`] = diagnosisStr
                  } else if (nodeName === "centerName") {
                    temp[`${nodeName}`] = userDetail?.currentCenter?.centerName
                  }
                }
              })
            })
            //To check for previously filled data
            const temp1 = savedForms.find(
              (each: any) => each?.formTemplateName === selectedForm?.subType
            )
            setFormData(temp1?.data ? { ...temp1?.data, ...temp } : { ...temp })
          }
        })
        .catch((err: any) => console.error(err?.message))
    }
  }, [selectedForm])

  return (
    <>
      {(fetchingMandatoryForms || fetchingSavedForms) ? (
        <div className="position-absolute" >
          <div
            className="spinner-border"
            style={{ width: "3rem", height: "3rem" }}
            role="status"
          />
        </div >
      ) : showModal ? (
        <Modal
          size="xl"
          show={visible}
          onHide={() => {
            setShowModal(false)
            closeModal()
          }}
        >
          <Modal.Header closeButton>
            <Modal.Title>
              <Row>
                <Col className="basic-patient-details">
                  {PermissionUtils.displayPatientName(
                    userRole,
                    patientDetail,
                    false,
                    null,
                    "-"
                  )}
                  {patientDetail?.mocId}-{patientDetail?.gender}-
                  {calculateAge(patientDetail?.dob)}
                </Col>
              </Row>
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Row className="heading-test">
              The following forms & consents are mandatory to proceed further
              {pendingForms &&
                pendingForms.map((each: any) => (
                  <li
                    className="mandatory-item"
                    onClick={() => setSelectedForm(each)}
                  >
                    {each?.subType}
                  </li>
                ))}
            </Row>
            {
              displayToast ? (
                <div>
                  <Toast
                    textMessage={displayToast.text}
                    type={displayToast.type}
                    onCloseText={"Close"}
                    onClose={() => {
                      setDisplayToast(null)
                      window.location.reload()
                    }}
                  ></Toast>
                </div>
              ) : null
            }
            {
              saved ? (
                <Toast
                  textMessage="Saved successfully"
                  type="success"
                  onCloseText={"Close"}
                  onClose={() => {
                    setSaved(false)
                    if (!visible) {
                      window.location.reload()
                    } else {
                      setShowChildren(true)
                    }
                  }}
                ></Toast>
              ) : null
            }
            <Box sx={{ width: "100%", marginTop: "20px" }}>
              {formTemplate && (
                <div className="mandatory-consent-form">
                  <SurveyComponent
                    css={defaultSurveyConfig.defaultSurveyCSS}
                    data={formData}
                    json={formTemplate}
                    onComplete={(survey: any) => {
                      handleFormComplete(survey?.data)
                    }}
                    hideDot={true}
                  />
                </div>
              )}
            </Box>
          </Modal.Body>
          <Modal.Footer></Modal.Footer>
        </Modal>
      ) : saved ? (
        <Toast
          textMessage="Saved successfully"
          type="success"
          onCloseText={"Close"}
          onClose={() => {
            setSaved(false)

            if (!visible) {
              window.location.reload()
            } else {
              setShowChildren(true)
            }
          }}
        ></Toast>
      ) : accessDenied ? (
        <Toast
          textMessage="You do not have permission"
          type="warning"
          onCloseText={"Close"}
          onClose={() => {
            setAccessDenied(false)
            setShowModal(true)
            setSelectedForm("")
          }}
        ></Toast>
      ) : <>
        {((showWarningOnly && visible) || (!appointmentDetails?.arrivalTime)) && (
          <div className="mt-5">
            {!appointmentDetails?.arrivalTime && (
              <Alert variant="danger">
                <div className='d-flex justify-content-between'>
                  <div className='d-flex'>
                    <FontAwesomeIcon icon={faArrowRightToCity} className='me-2' size='lg' />
                    <div>
                      <h6>Patient is not CHECKED IN!</h6>
                      <div>Patient needs to be checked in to fully access the software.</div>
                    </div>
                  </div>
                </div>
              </Alert>
            )}
            {showWarningOnly && visible && (
              <Alert variant="warning">
                <div className='d-flex justify-content-between'>
                  <div className='d-flex'>
                    <FontAwesomeIcon icon={faCircleExclamation} className='me-2' size='lg' />
                    <div>
                      <h6>Mandatory Forms</h6>
                      <div>Some mandatory forms are incomplete. Please fill them out to fully access the software.</div>
                    </div>
                  </div>
                  <Button
                    className='m-2'
                    variant='warning'
                    onClick={() => {
                      setShowModal(true)
                    }}
                    disabled={readOnly}
                  >
                    Complete Forms
                  </Button>
                </div>
              </Alert>
            )}
          </div>
        )}
        {showChildren ? <> {children} </> : <></>}
      </>}
    </>
  )
}

export default MandatoryConsentForm